<script lang="ts">
	import { onMount } from 'svelte';
	import { DEMO_ARTICLE, DEMO_FILE_NAME } from '#lib/demo';
	import { documentFromText } from '#lib/extract/plain';
	import { analyzeDocument, type AnalysisResult } from '#lib/report/engine';
	import type { ExtractedDocument } from '#lib/types';
	import { analyzeFile, type PipelineProgress } from '#lib/pipeline';
	import { toRepError, RepError, type RepError as RepErrorType } from '#lib/errors';
	import { initLang, t } from '#lib/i18n/state.svelte';
	import { formatCount } from '#lib/ui/labels';
	import Dropzone from '#lib/components/Dropzone.svelte';
	import Processing from '#lib/components/Processing.svelte';
	import ReportView from '#lib/components/ReportView.svelte';
	import TestSetPanel from '#lib/components/TestSetPanel.svelte';
	import LangToggle from '#lib/components/LangToggle.svelte';

	type View = 'landing' | 'processing' | 'report' | 'error';

	let view = $state<View>('landing');
	let progress = $state<PipelineProgress>({ stageKey: 'proc.stage.starting', ratio: 0, phase: 'extract' });
	let fileName = $state('');
	let error = $state<RepErrorType | null>(null);
	let analysis = $state<AnalysisResult | null>(null);
	let doc = $state<ExtractedDocument | null>(null);
	let pasteOpen = $state(false);
	let pasteText = $state('');
	let isDemo = $state(false);

	const checks = $derived([
		{ key: 'stat', icon: 'Σ', title: t('checks.stat.title'), body: t('checks.stat.body'), weight: 35 },
		{ key: 'cite', icon: '”', title: t('checks.cite.title'), body: t('checks.cite.body'), weight: 25 },
		{ key: 'struct', icon: '¶', title: t('checks.struct.title'), body: t('checks.struct.body'), weight: 20 },
		{ key: 'lang', icon: 'Aa', title: t('checks.lang.title'), body: t('checks.lang.body'), weight: 15 },
		{ key: 'prov', icon: '⌗', title: t('checks.prov.title'), body: t('checks.prov.body'), weight: 5 }
	]);

	const phase = $derived(view);
	const errorText = $derived(error ? t(error.code, error.params) : '');

	onMount(() => {
		initLang();
	});

	async function handleFile(file: File) {
		fileName = file.name;
		isDemo = false;
		view = 'processing';
		error = null;
		progress = { stageKey: 'proc.stage.preparing', ratio: 0, phase: 'extract' };
		try {
			const result = await analyzeFile(file, (p) => (progress = p));
			analysis = result.analysis;
			doc = result.doc;
			view = 'report';
			window.scrollTo({ top: 0, behavior: 'instant' });
		} catch (err) {
			error = toRepError(err);
			view = 'error';
		}
	}

	function loadDemo() {
		fileName = DEMO_FILE_NAME;
		isDemo = true;
		view = 'processing';
		error = null;
		progress = { stageKey: 'proc.stage.demoPreparing', ratio: 0.15, phase: 'extract' };
		setTimeout(() => {
			try {
				const d = documentFromText(DEMO_ARTICLE, DEMO_FILE_NAME, 'txt');
				progress = { stageKey: 'proc.stage.analyzing', ratio: 0.7, phase: 'analyze' };
				const a = analyzeDocument(d);
				analysis = a;
				doc = d;
				view = 'report';
				window.scrollTo({ top: 0, behavior: 'instant' });
			} catch (err) {
				error = toRepError(err);
				view = 'error';
			}
		}, 220);
	}

	function analyzePasted() {
		if (pasteText.trim().length < 100) {
			error = new RepError('err.tooShort');
			return;
		}
		pasteOpen = false;
		isDemo = false;
		fileName = t('paste.fileName');
		view = 'processing';
		progress = { stageKey: 'proc.stage.analyzing', ratio: 0.5, phase: 'analyze' };
		setTimeout(() => {
			try {
				const d = documentFromText(pasteText, t('paste.fileNameTxt'), 'txt');
				analysis = analyzeDocument(d);
				doc = d;
				view = 'report';
				window.scrollTo({ top: 0, behavior: 'instant' });
			} catch (err) {
				error = toRepError(err);
				view = 'error';
			}
		}, 180);
	}

	function reset() {
		view = 'landing';
		analysis = null;
		doc = null;
		error = null;
		fileName = '';
		pasteText = '';
		isDemo = false;
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	function retry() {
		view = 'landing';
		error = null;
	}
</script>

<svelte:head>
	<title>{t('meta.title')}</title>
	<meta name="description" content={t('meta.description')} />
</svelte:head>

{#if phase === 'report' && analysis && doc}
	<ReportView {analysis} {doc} onreset={reset} {isDemo} />
{:else}
	<div class="shell">
		<header class="topbar">
			<a class="brand" href="/" aria-label={t('brand.homeAria')} onclick={(e) => { e.preventDefault(); reset(); }}>
				<span class="logo" aria-hidden="true">R<span class="logo-dot"></span></span>
				<span class="brand-name">RepCheck</span>
				<span class="brand-tag">{t('common.beta')}</span>
			</a>
			<div class="top-actions">
				<LangToggle compact />
				<button class="btn btn-ghost btn-sm" onclick={() => (pasteOpen = !pasteOpen)}>
					{t('nav.paste')}
				</button>
				<a class="btn btn-sm" href="#test-set">{t('nav.testSet')}</a>
			</div>
		</header>

		<main class="main">
			<section class="hero">
				<p class="kicker">
					<span class="pulse" aria-hidden="true"></span>
					{t('hero.kicker')}
				</p>
				<h1>
					{t('hero.titleA')} <em>{t('hero.titleEm')}</em> {t('hero.titleB')}
				</h1>
				<p class="lede">
					{t('hero.ledeA')}
					<strong>{t('hero.ledeStrong')}</strong>
					{t('hero.ledeB')}
				</p>

				<div class="cta-row">
					<Dropzone onfile={handleFile} disabled={phase === 'processing'} />
				</div>

				<div class="quick">
					<span class="quick-label">{t('demo.quickLabel')}</span>
					<button class="btn btn-sm" onclick={loadDemo} disabled={phase === 'processing'}>
						{t('demo.cta')}
					</button>
					<span class="quick-note">{t('demo.note')}</span>
				</div>

				{#if view === 'processing'}
					<div class="processing-wrap">
						<Processing {fileName} {progress} />
					</div>
				{/if}

				{#if view === 'error'}
					<div class="error-card card" role="alert">
						<h2>{t('error.title')}</h2>
						<p>{errorText}</p>
						<div class="row">
							<button class="btn" onclick={retry}>{t('error.retry')}</button>
							<button class="btn btn-ghost" onclick={loadDemo}>{t('error.demo')}</button>
						</div>
					</div>
				{/if}

				{#if pasteOpen}
					<div class="paste card">
						<div class="row-between">
							<h2>{t('paste.title')}</h2>
							<button
								class="btn btn-ghost btn-sm"
								onclick={() => (pasteOpen = false)}
								aria-label={t('common.close')}
							>
								✕
							</button>
						</div>
						<p class="small muted">{t('paste.hint')}</p>
						<textarea
							bind:value={pasteText}
							rows="10"
							placeholder={t('paste.placeholder')}
						></textarea>
						<div class="row-between">
							<span class="small muted">{t('paste.chars', { n: formatCount(pasteText.length) })}</span>
							<button class="btn btn-primary" onclick={analyzePasted} disabled={pasteText.trim().length < 100}>
								{t('paste.analyze')}
							</button>
						</div>
					</div>
				{/if}
			</section>

			<section class="checks" aria-labelledby="checks-title">
				<div class="section-head">
					<h2 id="checks-title">{t('checks.title')}</h2>
					<p>{t('checks.subtitle')}</p>
				</div>
				<ul class="check-grid">
					{#each checks as c (c.key)}
						<li class="check card">
							<span class="check-icon" aria-hidden="true">{c.icon}</span>
							<h3>{c.title}</h3>
							<p>{c.body}</p>
							<span class="check-weight">{t('checks.weight', { n: c.weight })}</span>
						</li>
					{/each}
				</ul>
			</section>

			<section class="sample-banner card">
				<div>
					<p class="kicker">{t('sample.kicker')}</p>
					<h2>{t('sample.title')}</h2>
					<p class="small">
						{t('sample.bodyA')}<span class="mono">p , .001</span>{t('sample.bodyB')}
					</p>
				</div>
				<button class="btn btn-primary" onclick={loadDemo}>{t('sample.open')}</button>
			</section>

			<section class="privacy" aria-labelledby="privacy-title">
				<div class="privacy-copy">
					<h2 id="privacy-title">{t('privacy.title')}</h2>
					<p>{t('privacy.body')}</p>
					<ul class="privacy-list">
						<li>{t('privacy.li1')}</li>
						<li>{t('privacy.li2')}</li>
						<li>{t('privacy.li3')}</li>
					</ul>
				</div>
				<div class="privacy-art" aria-hidden="true">
					<div class="shield">
						<svg viewBox="0 0 64 64" width="72" height="72">
							<path
								d="M32 6 54 14v18c0 14-9.5 22.5-22 26C19.5 54.5 10 46 10 32V14z"
								fill="none"
								stroke="currentColor"
								stroke-width="2.4"
								stroke-linejoin="round"
							/>
							<path
								d="m22 32 7 7 13-14"
								fill="none"
								stroke="currentColor"
								stroke-width="2.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</div>
				</div>
			</section>

			<section id="test-set" class="test-set">
				<TestSetPanel />
			</section>
		</main>

		<footer class="footer">
			<div class="wrap footer-inner">
				<span>{t('footer.tagline')}</span>
				<span class="muted small">{t('footer.note')}</span>
			</div>
		</footer>
	</div>
{/if}

<style>
	.shell {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	}
	.topbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding: 0.85rem clamp(1rem, 3vw, 2.25rem);
		border-bottom: 1px solid var(--line);
		background: rgb(250 248 244 / 0.9);
		backdrop-filter: blur(8px);
		position: sticky;
		top: 0;
		z-index: 30;
	}
	.brand {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
		text-decoration: none;
		color: var(--ink);
	}
	.logo {
		position: relative;
		display: grid;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--ink);
		color: var(--paper);
		font-family: var(--font-display);
		font-size: 19px;
		line-height: 1;
		font-style: italic;
		padding-bottom: 2px;
	}
	.logo-dot {
		position: absolute;
		right: 4px;
		bottom: 4px;
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: #14b8a6;
	}
	.brand-name {
		font-family: var(--font-display);
		font-size: 1.3rem;
		letter-spacing: -0.01em;
	}
	.brand-tag {
		font-size: 0.62rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: var(--accent-ink);
		background: var(--accent-soft);
		border-radius: 99px;
		padding: 0.1rem 0.4rem;
	}
	.top-actions {
		display: flex;
		gap: 0.4rem;
	}
	.main {
		flex: 1;
		width: 100%;
		max-width: var(--maxw);
		margin-inline: auto;
		padding: 0 clamp(1rem, 3vw, 2.25rem) 4rem;
	}
	.hero {
		padding: clamp(2.5rem, 7vw, 5rem) 0 2.5rem;
		max-width: 880px;
		margin-inline: auto;
		text-align: center;
		animation: fade-up 0.6s var(--ease) both;
	}
	.kicker {
		display: inline-flex;
		align-items: center;
		gap: 0.45rem;
		font-size: 0.76rem;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--accent-ink);
		background: var(--accent-soft);
		border: 1px solid #c7e3dd;
		border-radius: 99px;
		padding: 0.25rem 0.75rem;
		margin: 0 0 1.4rem;
	}
	.pulse {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: var(--accent);
		animation: pulse-ring 2.4s infinite;
	}
	.hero h1 {
		margin: 0 0 1.1rem;
	}
	.hero h1 em {
		font-style: italic;
		color: var(--accent-ink);
	}
	.lede {
		font-size: clamp(1rem, 1.5vw, 1.1rem);
		color: var(--ink-2);
		max-width: 62ch;
		margin: 0 auto 2rem;
	}
	.cta-row {
		max-width: 660px;
		margin-inline: auto;
	}
	.quick {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-top: 1rem;
		font-size: 0.82rem;
	}
	.quick-label {
		color: var(--muted);
	}
	.quick-note {
		color: var(--muted);
		font-size: 0.76rem;
	}
	.processing-wrap {
		margin-top: 1.5rem;
	}
	.error-card {
		margin: 1.5rem auto 0;
		max-width: 560px;
		padding: 1.3rem 1.4rem;
		text-align: left;
		border-left: 3px solid var(--critical);
	}
	.error-card h2 {
		font-family: var(--font-ui);
		font-size: 1rem;
		font-weight: 650;
		margin: 0 0 0.4rem;
	}
	.error-card p {
		font-size: 0.88rem;
		color: var(--ink-2);
	}
	.paste {
		margin: 1.5rem auto 0;
		max-width: 660px;
		padding: 1.2rem 1.3rem;
		text-align: left;
	}
	.paste h2 {
		font-family: var(--font-ui);
		font-size: 1rem;
		font-weight: 650;
		margin: 0;
	}
	.paste textarea {
		width: 100%;
		margin: 0.7rem 0;
		border: 1px solid var(--line-strong);
		border-radius: 10px;
		padding: 0.7rem 0.8rem;
		font-family: var(--font-mono);
		font-size: 0.82rem;
		line-height: 1.55;
		resize: vertical;
		background: #fff;
	}
	.checks {
		padding: 2.5rem 0 1rem;
	}
	.section-head {
		text-align: center;
		margin-bottom: 1.6rem;
	}
	.section-head h2 {
		margin: 0 0 0.3rem;
	}
	.section-head p {
		color: var(--muted);
		font-size: 0.9rem;
		margin: 0;
	}
	.check-grid {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: 0.75rem;
	}
	@media (max-width: 1000px) {
		.check-grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}
	@media (max-width: 560px) {
		.check-grid {
			grid-template-columns: 1fr;
		}
	}
	.check {
		padding: 1.1rem 1.05rem;
		display: flex;
		flex-direction: column;
		gap: 0.3rem;
		align-items: flex-start;
	}
	.check-icon {
		font-size: 1.15rem;
		color: var(--accent-ink);
		background: var(--accent-soft);
		border-radius: 8px;
		width: 32px;
		height: 32px;
		display: grid;
		place-items: center;
		font-weight: 700;
		margin-bottom: 0.2rem;
	}
	.check h3 {
		font-family: var(--font-ui);
		font-size: 0.88rem;
		font-weight: 650;
		margin: 0;
	}
	.check p {
		font-size: 0.8rem;
		color: var(--ink-2);
		margin: 0;
		line-height: 1.5;
	}
	.check-weight {
		margin-top: auto;
		padding-top: 0.4rem;
		font-size: 0.7rem;
		font-weight: 600;
		color: var(--muted);
		letter-spacing: 0.03em;
	}
	.sample-banner {
		margin: 1.5rem 0;
		padding: 1.3rem 1.4rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1.5rem;
		flex-wrap: wrap;
		background:
			radial-gradient(100% 160% at 100% 0%, rgb(251 240 220 / 0.9), transparent 60%),
			var(--card);
	}
	.sample-banner h2 {
		font-size: 1.25rem;
		margin: 0.1rem 0 0.35rem;
	}
	.sample-banner p {
		margin: 0;
		max-width: 60ch;
		color: var(--ink-2);
	}
	.sample-banner .kicker {
		background: var(--warning-soft);
		color: var(--warning);
		border-color: #ecd6ac;
		margin: 0;
	}
	.privacy {
		margin: 1.5rem 0;
		padding: clamp(1.4rem, 3vw, 2.2rem);
		border: 1px solid var(--line);
		border-radius: var(--radius);
		display: grid;
		grid-template-columns: 1fr 200px;
		gap: 1.5rem;
		align-items: center;
		background: var(--paper-2);
	}
	@media (max-width: 720px) {
		.privacy {
			grid-template-columns: 1fr;
		}
		.privacy-art {
			display: none;
		}
	}
	.privacy h2 {
		margin: 0 0 0.5rem;
	}
	.privacy p {
		color: var(--ink-2);
		max-width: 62ch;
	}
	.privacy-list {
		margin: 0.6rem 0 0;
		padding-left: 1.1rem;
		font-size: 0.86rem;
		color: var(--ink-2);
		display: grid;
		gap: 0.25rem;
	}
	.privacy-art {
		display: grid;
		place-items: center;
	}
	.shield {
		color: var(--accent-ink);
		background: #fff;
		border: 1px solid var(--line);
		border-radius: 50%;
		width: 118px;
		height: 118px;
		display: grid;
		place-items: center;
		box-shadow: var(--shadow-sm);
	}
	.test-set {
		margin-top: 1.5rem;
		scroll-margin-top: 90px;
	}
	.footer {
		border-top: 1px solid var(--line);
		padding: 1.4rem 0 2rem;
		margin-top: 2rem;
	}
	.footer-inner {
		display: flex;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
		font-size: 0.8rem;
		color: var(--muted);
	}
</style>
